import * as React from 'react';
import ApEditorMode from '../../enums/ApEditorMode';
import { ApEditorActions, ApLocation } from '../../sic-lib/reducers/actionPlanEditorReducer';
import { ApActionBase } from '../../sic-lib/types/ActionPlan';
import ApEditorActionCreate from './ApEditorActionCreate/ApEditorActionCreate';
import ApEditorActionModify from './ApEditorActionModify/ApEditorActionModify';
import ApEditorTimeline from './ApEditorTimeline/ApEditorTimeline';

interface Props {
    apRootId: number;
    apLocation: ApLocation[];
    apActions: ApActionBase[];
    apEditMode: ApEditorMode;
    apEditIndex: number;
    apDispatch: React.Dispatch<ApEditorActions>;
    apContactId?: number;
}

/**
 * ActionPlanEditor
 *
 * @description
 * Top-level component to access the component tree which edits, creates Action Plan templates and plans
 *
 * @hidden
 * TIP: Parent component should implement actionPlanEditorReducer with React.useReducer to manage state.
 * (Designed to accomodate updating in Communicator component tree while keeping ActionPlanEditor
 * component tree free from logic that has nothing to do with the general task of editing action plans).
 */
export default function ActionPlanEditor({
    apRootId,
    apLocation,
    apActions,
    apEditMode,
    apEditIndex,
    apDispatch,
    apContactId = undefined,
}: Props) {
    return (
        <React.Fragment>
            {
                /*
                    Editing an existing ApAction

                    TIP: for the conditional to work, ensure saved action plans always
                    have an actionId property that is not falsey
                 */
                apEditIndex > -1 && apActions[apEditIndex].actionId !== undefined && (
                    <ApEditorActionModify
                        rootId={apRootId}
                        action={apActions[apEditIndex]}
                        actionIndex={apEditIndex}
                        apEditMode={apEditMode}
                        apDispatch={apDispatch}
                    />
                )
            }
            {
                /* 
                    Editing an unsaved ApAction 

                    TIP: for the conditional to work, ensure saved action plans always 
                    have an actionId property that is not falsey
                 */
                apEditIndex > -1 && apActions[apEditIndex].actionId === undefined && (
                    <ApEditorActionCreate
                        rootId={apRootId}
                        action={apActions[apEditIndex]}
                        actionIndex={apEditIndex}
                        apEditMode={apEditMode}
                        apDispatch={apDispatch}
                    />
                )
            }
            {
                /* No active edits; displaying all actions as a timeline */
                apEditIndex < 0 && (
                    <ApEditorTimeline
                        contactId={apContactId}
                        apRootId={apRootId}
                        apLocation={apLocation}
                        actions={apActions}
                        apEditMode={apEditMode}
                        apDispatch={apDispatch}
                    />
                )
            }
        </React.Fragment>
    );
}
